"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import type { AuthContextProps } from "react-oidc-context";

import GlobalErrorPage from "@/components/global-error-page";
import { logError } from "@/lib/logError";
import { removeLoader } from "@/splashScreen";

import { clearSigninRedirectAttempts } from "./signinRedirectAttempts";

interface ParkedSignInProps {
  signinRedirect?: AuthContextProps["signinRedirect"];
}

/**
 * What the customer sees when the automatic signin-redirect budget is spent
 * (resolveAuthAction "redirect-budget-exhausted"). Until 2026-09-17 this state
 * rendered NOTHING: after a soft navigation from a public page the splash veil
 * was already hidden and AuthController returned undefined — a blank page with
 * no way out (CUSTOMER-UI-6V). The button is a deliberate user action: it
 * resets the budget and goes straight to Keycloak, mirroring
 * hooks/useDeliberateSignIn (this component receives AuthController's
 * `signinRedirect` prop so the E2E stub applies here too).
 */
export function ParkedSignIn({ signinRedirect }: ParkedSignInProps) {
  const t = useTranslations("authParked");

  // A fresh load of "/" arrives with the veil still up; drop it so this
  // screen is visible. Idempotent — the register layout may already have.
  useEffect(() => {
    removeLoader();
  }, []);

  return (
    <div className="gradient-glow-bg flex min-h-dvh flex-col items-center justify-center gap-6 px-2 lg:px-0">
      <GlobalErrorPage
        title={t("title")}
        subtitle={t("subtitle")}
        cta={t("cta")}
        onClick={() => {
          clearSigninRedirectAttempts();
          void signinRedirect?.({
            redirect_uri: window.location.origin,
          }).catch((error: unknown) => {
            logError(
              "[Auth] Sign-in from the parked screen did not start",
              error
            );
          });
        }}
      />
    </div>
  );
}
